"use client"

import { useEffect, useState, type ReactNode } from "react"
import { useTranslations } from "next-intl"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { getBankAccounts } from "@/lib/bank-accounts-api"
import { getMoneyAccounts, type MoneyAccount } from "@/lib/money-api"
import { cn } from "@/lib/utils"
import type { BankAccount, PaymentMethod } from "@/types"

/**
 * ArtDent 260924 — methods whose money lands in one of the clinic's bank
 * accounts. Mirrors Payment::METHODS_WITH_BANK; the server nulls the account
 * on any other method, so this only decides when the picker is shown.
 */
export const methodUsesBank = (method: PaymentMethod | string): boolean =>
  method === "card" || method === "bank-transfer"

/**
 * ArtDent 260929 — the bank a payment row names. The account picked from the
 * Bank accounts list wins (payments recorded before Cash & banks was
 * activated); otherwise it is the Cash & banks account the money was received
 * into. Cash is in the drawer, so a register's account never reads as a bank.
 */
export const paymentBankName = (payment: {
  method: PaymentMethod | string
  bank_account?: { name: string } | null
  money_account?: { name: string } | null
}): string | null =>
  payment.bank_account?.name ??
  (methodUsesBank(payment.method) ? (payment.money_account?.name ?? null) : null)

/** Radix Select cannot hold "" — this stands for "not specified". */
const NONE = "none"

/**
 * The clinic's active accounts, fetched while `enabled` (the dialog is open).
 * An empty list — every clinic that has not set any up — hides the picker, so
 * their payment forms are unchanged.
 */
export function useBankAccounts(enabled: boolean): BankAccount[] {
  const [accounts, setAccounts] = useState<BankAccount[]>([])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    getBankAccounts({ activeOnly: true })
      .then((rows) => {
        if (!cancelled) setAccounts(rows)
      })
      // A 403/404 (no payments module, no read grant) just means no picker.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [enabled])

  return accounts
}

/**
 * ArtDent 260929 — the Cash & banks accounts a POS or transfer can land in,
 * for the Payments list filter. Empty on a clinic without Cash & banks, and
 * for a user who may not see its accounts.
 */
export function useMoneyBankAccounts(enabled: boolean): MoneyAccount[] {
  const [accounts, setAccounts] = useState<MoneyAccount[]>([])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    getMoneyAccounts()
      .then((rows) => {
        if (!cancelled) setAccounts(rows.filter((account) => account.kind !== "cash"))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [enabled])

  return accounts
}

interface BankAccountFieldProps {
  id: string
  accounts: BankAccount[]
  value: string | null
  onChange: (value: string | null) => void
  /**
   * The account the payment already names, when editing. A retired account is
   * missing from the active list but must still read as selected rather than
   * silently blank.
   */
  current?: { id: string; name: string } | null
  disabled?: boolean
  /** Host dialogs with their own label style pass it in; defaults to <Label>. */
  renderLabel?: (text: string) => ReactNode
  triggerClassName?: string
}

export function BankAccountField({
  id,
  accounts,
  value,
  onChange,
  current,
  disabled,
  renderLabel,
  triggerClassName,
}: BankAccountFieldProps) {
  const t = useTranslations("payments")

  const options =
    current && !accounts.some((a) => a.id === current.id)
      ? [...accounts, { id: current.id, name: current.name }]
      : accounts

  if (options.length === 0) return null

  return (
    <div className={renderLabel ? undefined : "space-y-2"}>
      {renderLabel ? (
        renderLabel(t("bankAccount.label"))
      ) : (
        <Label htmlFor={id}>{t("bankAccount.label")}</Label>
      )}
      <Select
        value={value ?? NONE}
        onValueChange={(v) => onChange(v === NONE ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger id={id} className={cn("w-full", triggerClassName)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{t("bankAccount.none")}</SelectItem>
          {options.map((account) => (
            <SelectItem key={account.id} value={account.id}>
              {account.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
